import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Row,
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';
import Link from 'next/link';
import { IAreaGraph, ITransaction } from '@/types/explorer';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/shadcn/table';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/components/shadcn/card';
import { sanitizeUsername } from '@/utils/strings';
import { prettyDate } from '@/utils/dates';
import Contribute from '@/components/ui/shared/Contribute';
import { Button } from '@/components/shadcn/button';
import { getTransactions } from '@/api/transactions';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/components/shadcn/breadcrumb';
import { getContributorStats } from '@/api/contributors';
import {
  formatNumber,
  getDefaultByWeekData,
  getTotalKm,
  transformContributorStats,
} from '@/utils/explorer';
import HoneyIcon from '../icons/HoneyIcon';
import CustomTooltip from '../charts/CustomTooltip';
import AreaChart from '../charts/AreaChart';

const LIMIT = 100;

const columnHelper = createColumnHelper<ITransaction>();
const cols = [
  {
    id: 'amount',
    header: 'HONEY',
    enableSorting: true,
    cell: ({ row }: { row: Row<ITransaction> }) => (
      <div className="flex gap-2 overflow-hidden text-ellipsis">
        <HoneyIcon className="size-5 shrink-0" />
        <span className="text-xs md:text-sm">{row.getValue('amount')}</span>
      </div>
    ),
  },
  {
    id: 'type',
    header: 'Reward Type',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<ITransaction> }) => (
      <div className="text-xs md:text-sm">{row.getValue('type')}</div>
    ),
  },
  {
    id: 'createdAt',
    header: 'Date',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<ITransaction> }) => (
      <div className="text-xs md:text-sm">
        {prettyDate(row.getValue('createdAt'))}
      </div>
    ),
  },
  {
    id: 'signature',
    header: 'Transaction',
    enableSorting: true,
    sortDescFirst: true,
    cell: ({ row }: { row: Row<ITransaction> }) => (
      <Link
        target="_blank"
        href={`https://explorer.solana.com/tx/${row.getValue('signature')}`}
        className="block overflow-hidden text-ellipsis text-xs text-azure-700 md:text-sm">
        {row.getValue('signature')}
      </Link>
    ),
  },
];

interface Props {
  username: string;
}

export default function Contributor(props: Props) {
  const { username } = props;
  const [transactions, setTransactions] = useState<ITransaction[]>([]);
  const [pagination, setPagination] = useState({
    pageSize: LIMIT,
    pageIndex: 0,
  });
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokensByWeek, setTokensByWeek] = useState<IAreaGraph[]>([]);
  const [coverageByWeek, setCoverageByWeek] = useState<IAreaGraph[]>([]);
  const [mountReviewByWeek, setMountReviewByWeek] = useState<IAreaGraph[]>([]);

  const currentTokens =
    tokensByWeek[(tokensByWeek.length || 1) - 1]?.value || 0;
  const currentCoverage =
    coverageByWeek[(tokensByWeek.length || 1) - 1]?.value || 0;
  const currentMountReview =
    mountReviewByWeek[(mountReviewByWeek.length || 1) - 1]?.value || 0;

  const columns = useMemo(
    () =>
      cols.map(({ id, header, cell, enableSorting, sortDescFirst }: any) => ({
        ...columnHelper.accessor(id, {
          header,
        }),
        cell,
        enableSorting,
        sortDescFirst,
      })),
    [],
  );

  const fetchList = useCallback(
    async (skip = 0, username: string) => {
      setIsLoading(true);
      const _transactions: ITransaction[] = await getTransactions(
        LIMIT,
        skip,
        username,
      );
      setHasNextPage(_transactions.length === LIMIT);
      setTransactions(_transactions);
      setIsLoading(false);
      setIsDataLoaded(true);
    },
    [setTransactions],
  );

  useEffect(() => {
    fetchList(pagination.pageIndex * LIMIT, username);
  }, [fetchList, pagination.pageIndex, username]);

  const fetchStats = useCallback(async (username: string) => {
    setIsLoading(true);
    const stats = await getContributorStats(username);
    if (stats) {
      const _tokensByWeek = transformContributorStats(
        stats.tokensByWeek || getDefaultByWeekData(0),
      );
      const _mountReviewByWeek = transformContributorStats(
        stats.mountReviews || getDefaultByWeekData(0),
      );
      const _coverageByWeek = transformContributorStats(
        stats.totalL12ByWeek || getDefaultByWeekData(0),
        ({ date, value }) => ({
          date,
          value: getTotalKm(value || 0),
        }),
      );
      setTokensByWeek(_tokensByWeek);
      setCoverageByWeek(_coverageByWeek);
      setMountReviewByWeek(_mountReviewByWeek);
    } else {
      setTokensByWeek(transformContributorStats(getDefaultByWeekData(0)));
      setCoverageByWeek(transformContributorStats(getDefaultByWeekData(0)));
      setMountReviewByWeek(transformContributorStats(getDefaultByWeekData(0)));
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    fetchStats(username);
  }, [username, fetchStats]);

  const tableData = React.useMemo(
    () => (isLoading ? [] : transactions),
    [isLoading, transactions],
  );

  const table = useReactTable({
    data: tableData,
    columns: columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    onPaginationChange: setPagination,
    rowCount: -1,
    state: {
      pagination,
    },
  });

  const from =
    table.getState().pagination.pageIndex *
      table.getState().pagination.pageSize +
    1;
  const to =
    from +
    Math.min(
      (table.getState().pagination.pageIndex + 1) *
        table.getState().pagination.pageSize,
      transactions.length - 1,
    );

  return (
    <div className="flex h-full flex-col lg:flex-row">
      <div className="flex h-1/2 w-full shrink-0 flex-col gap-2 px-4 py-2 md:gap-4 md:p-4 lg:h-full lg:w-1/2">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link href="/network/contributors">Contributors</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>{sanitizeUsername(username)}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="no-scrollbar h-full flex-1 overflow-scroll rounded-md border [&>div]:overflow-visible">
          <Table className="table-fixed">
            <TableHeader className="sticky top-0 bg-neutral-800">
              {table.getHeaderGroups().map(headerGroup => (
                <TableRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, index) => {
                    return (
                      <TableHead
                        data-order={index}
                        key={header.id}
                        className="h-4 w-1/4 p-2 data-[order='2']:w-1/6 data-[order='0']:pl-10 md:h-12 md:px-4">
                        <div className="flex items-center gap-1 px-0 text-[10px] leading-none md:text-sm">
                          {flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                        </div>
                      </TableHead>
                    );
                  })}
                </TableRow>
              ))}
            </TableHeader>
            <TableBody>
              {isDataLoaded ? (
                isLoading ? null : transactions.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={4}
                      className="p-2 text-xs md:px-4 md:py-3 md:text-sm">
                      No transactions found
                    </TableCell>
                  </TableRow>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <TableRow
                      key={row.id}
                      className="leading-none hover:bg-neutral-900">
                      {row.getVisibleCells().map(cell => (
                        <TableCell
                          key={cell.id}
                          className="p-2 md:px-4 md:py-3">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                )
              ) : null}
            </TableBody>
          </Table>
        </div>
        {isDataLoaded ? (
          <div className="flex w-full items-center justify-end space-x-2 text-neutral-0">
            <div className="flex-1 text-[10px] md:text-xs">
              {from === 1 && to < LIMIT
                ? `Showing ${to} transaction${to === 1 ? '' : 's'}`
                : `Showing ${from} to ${to} transactions`}
            </div>
            <div className="space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={table.previousPage}
                className="text-[10px] md:text-xs"
                disabled={!table.getCanPreviousPage()}>
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="text-[10px] md:text-xs"
                onClick={table.nextPage}
                disabled={!hasNextPage}>
                Next
              </Button>
            </div>
          </div>
        ) : null}
      </div>
      <div className="no-scrollbar flex w-full flex-col gap-6 overflow-visible bg-neutral-700 p-4 text-neutral-0 lg:w-1/2 lg:overflow-auto">
        <div className="flex flex-1 flex-col gap-4">
          <h2 className="text-sm font-bold md:text-xl">
            {sanitizeUsername(username)}
          </h2>
          <div className="grid flex-1 grid-cols-2 gap-2 md:gap-4">
            <Card className="flex h-full flex-1 flex-col rounded-lg bg-neutral-900">
              <CardHeader className="pb-1">
                <CardTitle className="mb-0">HONEY Rewards</CardTitle>
              </CardHeader>
              <CardContent className="flex min-h-[200px] flex-1 flex-col ">
                <p className="mb-1 text-base uppercase text-azure-400 md:text-2xl">
                  {formatNumber(currentTokens, 'short', true) || 0}
                </p>
                <div className="size-full flex-1">
                  <AreaChart
                    data={tokensByWeek}
                    customTooltip={(props: any) => (
                      <CustomTooltip
                        line1={`${Math.round(
                          props?.payload[0]?.value || 0,
                        ).toLocaleString('en-US')} Total Tokens Earned`}
                        {...props}
                      />
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="flex h-full flex-1 flex-col rounded-lg bg-neutral-900">
              <CardHeader className="pb-1">
                <CardTitle className="mb-0">Total KM Mapped</CardTitle>
              </CardHeader>
              <CardContent className="flex min-h-[200px] flex-1 flex-col ">
                <p className="mb-1 text-base uppercase text-azure-400 md:text-2xl">
                  {formatNumber(currentCoverage, 'short', true) || 0}
                </p>
                <div className="size-full flex-1">
                  <AreaChart
                    data={coverageByWeek}
                    customTooltip={(props: any) => (
                      <CustomTooltip
                        line1={`${Math.round(
                          props?.payload[0]?.value || 0,
                        ).toLocaleString('en-US')} Total KM Mapped`}
                        {...props}
                      />
                    )}
                  />
                </div>
              </CardContent>
            </Card>
            <Card className="flex h-full flex-1 flex-col rounded-lg bg-neutral-900">
              <CardHeader className="pb-1">
                <CardTitle className="mb-0">Mount Rating</CardTitle>
              </CardHeader>
              <CardContent className="flex min-h-[200px] flex-1 flex-col ">
                <p className="mb-1 text-base uppercase text-azure-400 md:text-2xl">
                  {formatNumber(currentMountReview, 'short', true) || 0}
                </p>
                <div className="size-full flex-1">
                  <AreaChart
                    data={mountReviewByWeek}
                    customTooltip={(props: any) => (
                      <CustomTooltip
                        line1={`Mount Rating: ${Math.round(
                          props?.payload[0]?.value || 0,
                        ).toLocaleString('en-US')}`}
                        {...props}
                      />
                    )}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        <Contribute />
      </div>
    </div>
  );
}
